import React from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemIcon, ListItemText, Button } from '@mui/material';
import { FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Task, Document } from '../../../types';
import { 
  MedicalRecordReviewForm,
  ExpertWitnessForm,
  InterrogatoriesForm,
  InitialClientContactForm,
  BasicCaseAssessmentForm,
} from './index';
import { CompletedFormView } from './CompletedFormView';

interface TaskFormsTabProps {
  tasks: Task[];
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => void;
  selectedTaskId: string | null;
  onTaskSelect: (taskId: string | null) => void;
  onDocumentUpload?: (document: Omit<Document, 'id' | 'uploadDate'>) => void;
}

export const TaskFormsTab: React.FC<TaskFormsTabProps> = ({
  tasks,
  onTaskUpdate,
  selectedTaskId,
  onTaskSelect,
  onDocumentUpload,
}) => {
  const formTasks = tasks.filter(task => {
    return [
      'Initial Client Contact',
      'Basic Case Assessment',
      'Review Medical Records',
      'Schedule Expert Witness',
      'Draft Interrogatories'
    ].includes(task.title);
  });

  const selectedTask = selectedTaskId ? tasks.find(t => t.id === selectedTaskId) : null;

  const handleFormSubmit = (formData: any) => {
    if (selectedTask) {
      onTaskUpdate(selectedTask.id, {
        status: 'completed',
        metadata: {
          ...formData,
          completedAt: new Date().toISOString(),
        },
      });
      onTaskSelect(null);
    }
  };

  const renderForm = (task: Task) => {
    // If the task is completed, show the completed form view
    if (task.status === 'completed' && task.metadata) {
      return <CompletedFormView task={task} />;
    }

    // Otherwise, show the form for editing
    switch (task.title) {
      case 'Initial Client Contact':
        return <InitialClientContactForm task={task} onSubmit={handleFormSubmit} />;
      case 'Basic Case Assessment':
        return <BasicCaseAssessmentForm task={task} onSubmit={handleFormSubmit} />;
      case 'Review Medical Records':
        return <MedicalRecordReviewForm task={task} onSubmit={handleFormSubmit} />;
      case 'Schedule Expert Witness':
        return <ExpertWitnessForm task={task} onSubmit={handleFormSubmit} />;
      case 'Draft Interrogatories':
        return <InterrogatoriesForm task={task} onSubmit={handleFormSubmit} />;
      default:
        return null;
    }
  };

  if (formTasks.length === 0) {
    return (
      <Paper 
        sx={{ 
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <AlertCircle size={48} className="text-gray-400" />
        <Typography variant="h6" color="text.secondary">
          No Forms Available
        </Typography>
        <Typography color="text.secondary" align="center">
          There are no specialized forms for the current tasks.
          Forms will appear here when tasks requiring detailed information are created.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {!selectedTask ? (
        <Paper>
          <List>
            {formTasks.map((task) => (
              <ListItem
                key={task.id}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <ListItemIcon>
                  {task.status === 'completed' ? (
                    <CheckCircle className="text-green-500" />
                  ) : (
                    <FileText />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={task.title}
                  secondary={task.description}
                  primaryTypographyProps={{
                    fontWeight: 500,
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onTaskSelect(task.id)}
                >
                  {task.status === 'completed' ? 'View Form' : 'Fill Form'}
                </Button>
              </ListItem>
            ))}
          </List>
        </Paper>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {renderForm(selectedTask)}
          {selectedTask.status === 'completed' && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => onTaskSelect(null)}
                variant="outlined"
              >
                Back to Forms List
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};